import { Flex, Icon, Input, Separator, Spinner, Text } from '@chakra-ui/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Check, Search } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useGetPbxList, useGetPbxListInfinite } from '~/queries/pbxQueries'
import { getRecentPbxs } from '~/utils/recentPbxs'

import { PaginatedApiResult, Pbx } from '@mangovoice/types'

type PbxSummary = Pick<Pbx, 'id' | 'name'>

const RECENTS_THRESHOLD = 15
const MAX_RECENTS = 5

interface PbxPickerProps {
  currentPbx?: PbxSummary
  currentPbxId?: number
  onSelect: (pbx: PbxSummary) => void
  initialPbxList?: PaginatedApiResult<Pbx>
  isLoading?: boolean
}

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

export const PbxPicker = ({
  currentPbx,
  currentPbxId,
  onSelect,
  initialPbxList,
  isLoading: externalLoading,
}: PbxPickerProps) => {
  const resolvedCurrentPbxId = currentPbx?.id ?? currentPbxId
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  // Initial fetch to get total count (small page, just need count)
  const { data: countData, isLoading: countLoading } = useGetPbxList({
    apiParams: { limit: 1 },
    options: { enabled: !initialPbxList },
  })

  const totalCount = initialPbxList?.count ?? countData?.count ?? 0
  const isSmallList = totalCount <= RECENTS_THRESHOLD
  const showRecents = totalCount > RECENTS_THRESHOLD
  const loading = externalLoading ?? countLoading

  // For small lists, fetch everything in one shot
  const { data: smallListData } = useGetPbxList({
    apiParams: { limit: totalCount },
    options: { enabled: !initialPbxList && isSmallList && totalCount > 0 },
  })

  const smallPbxs = initialPbxList?.results ?? smallListData?.results ?? []

  // For large lists, use infinite query with server-side search
  const isSearching = !!debouncedSearch
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: infiniteLoading,
  } = useGetPbxListInfinite({
    search: isSearching ? debouncedSearch : undefined,
    enabled: !isSmallList && totalCount > 0,
  })

  const infinitePbxs = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.results) ?? [],
    [infiniteData],
  )
  const infiniteTotalCount = infiniteData?.pages[0]?.count ?? totalCount

  // Client-side filtering for small lists
  const filteredSmallList = useMemo(() => {
    if (!isSmallList) return []
    if (!search.trim()) return smallPbxs
    const q = search.toLowerCase()
    return smallPbxs.filter((pbx) => pbx.name.toLowerCase().includes(q))
  }, [isSmallList, smallPbxs, search])

  // Recents
  const recentPbxs: PbxSummary[] = useMemo(() => {
    if (!showRecents) return []
    return getRecentPbxs()
      .slice(0, MAX_RECENTS)
      .map((r) => ({ id: r.id, name: r.name }))
  }, [showRecents])

  const filteredRecents = useMemo(() => {
    if (!showRecents) return []
    const filtered = resolvedCurrentPbxId
      ? recentPbxs.filter((r) => r.id !== resolvedCurrentPbxId)
      : recentPbxs
    return filtered
  }, [showRecents, recentPbxs, resolvedCurrentPbxId])

  // All PBXs for the main list (deduped against current PBX)
  const mainListPbxs = useMemo(() => {
    if (isSmallList) return filteredSmallList
    return infinitePbxs
  }, [isSmallList, filteredSmallList, infinitePbxs])

  // Flat selectable items for keyboard navigation
  const selectableItems = useMemo<PbxSummary[]>(() => {
    const items: PbxSummary[] = []
    if (showRecents && filteredRecents.length > 0 && !isSearching) {
      items.push(...filteredRecents)
    }
    items.push(...mainListPbxs)
    return items
  }, [showRecents, filteredRecents, isSearching, mainListPbxs])

  // Keyboard navigation
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  // State rather than a ref: the virtualizer reads it during its own layout
  // effect, which runs before this parent's ref would be attached.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  )
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [selectableItems])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (selectableItems.length === 0) return

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev < selectableItems.length - 1 ? prev + 1 : 0,
          )
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : selectableItems.length - 1,
          )
          break
        }
        case 'Enter': {
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < selectableItems.length
          ) {
            e.preventDefault()
            onSelect(selectableItems[highlightedIndex])
          }
          break
        }
      }
    },
    [selectableItems, highlightedIndex, onSelect],
  )

  // Display count
  const displayCount = isSearching ? infiniteTotalCount : totalCount

  const showSearch = !isSmallList
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isSmallList) {
      containerRef.current?.focus()
    }
  }, [isSmallList])

  if (loading) {
    return (
      <Flex align="center" justify="center" py="8">
        <Spinner size="md" color="fg.subtle" />
      </Flex>
    )
  }

  return (
    <Flex
      ref={containerRef}
      direction="column"
      gap="2"
      onKeyDown={isSmallList ? handleKeyDown : undefined}
      tabIndex={isSmallList ? 0 : undefined}
      outline="none"
    >
      {/* Search */}
      {showSearch && (
        <Flex
          align="center"
          gap="2"
          px="3"
          py="2"
          borderWidth="1px"
          borderColor="border.emphasized"
          borderRadius="md"
          bg="bg.panel"
        >
          <Icon as={Search} boxSize="4" color="fg.subtle" />
          <Input
            ref={inputRef}
            variant="flushed"
            placeholder={t('pbxPicker.searchPlaceholder', 'Search PBXs...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            size="sm"
            borderBottom="none"
            _focus={{ borderBottom: 'none', boxShadow: 'none' }}
          />
        </Flex>
      )}

      {/* Count indicator */}
      {totalCount > 0 && (
        <Text fontSize="xs" color="fg.subtle" px="3">
          {isSearching
            ? t('pbxPicker.countFiltered', '{{count}} PBXs found', {
                count: displayCount,
              })
            : t('pbxPicker.count', '{{count}} PBXs', { count: displayCount })}
        </Text>
      )}

      {/* List */}
      <Flex
        direction="column"
        maxH="20rem"
        overflowY="auto"
        ref={setScrollElement}
        gap="0.5"
      >
        {isSmallList ? (
          <SmallListView
            pbxs={filteredSmallList}
            currentPbxId={resolvedCurrentPbxId}
            onSelect={onSelect}
            noResultsText={t('pbxPicker.noResults', 'No PBXs found')}
            highlightedPbxId={
              highlightedIndex >= 0
                ? selectableItems[highlightedIndex]?.id
                : undefined
            }
            scrollElement={scrollElement}
          />
        ) : (
          <LargeListView
            recentPbxs={filteredRecents}
            mainPbxs={mainListPbxs}
            isSearching={isSearching}
            searchLoading={infiniteLoading && isSearching}
            currentPbxId={resolvedCurrentPbxId}
            onSelect={onSelect}
            recentLabel={t('pbxPicker.recent', 'Recent')}
            allPbxsLabel={
              isSearching
                ? t('pbxPicker.searchResults', 'Search Results')
                : t('pbxPicker.allPbxs', 'All PBXs')
            }
            noResultsText={t('pbxPicker.noResults', 'No PBXs found')}
            highlightedPbxId={
              highlightedIndex >= 0
                ? selectableItems[highlightedIndex]?.id
                : undefined
            }
            scrollElement={scrollElement}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        )}
      </Flex>
    </Flex>
  )
}

function SectionHeader({ label }: { label: string }) {
  return (
    <Text
      fontSize="xs"
      fontWeight="600"
      color="fg.subtle"
      px="3"
      py="1"
      textTransform="uppercase"
      letterSpacing="wider"
    >
      {label}
    </Text>
  )
}

function PbxListItem({
  pbx,
  isCurrent,
  isHighlighted,
  onSelect,
  scrollElement,
}: {
  pbx: PbxSummary
  isCurrent: boolean
  isHighlighted?: boolean
  onSelect: (pbx: PbxSummary) => void
  scrollElement?: HTMLDivElement | null
}) {
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isHighlighted && itemRef.current && scrollElement) {
      const item = itemRef.current
      const container = scrollElement
      const itemTop = item.offsetTop
      const itemBottom = itemTop + item.offsetHeight
      const scrollTop = container.scrollTop
      const containerBottom = scrollTop + container.clientHeight

      if (itemTop < scrollTop) {
        container.scrollTop = itemTop
      } else if (itemBottom > containerBottom) {
        container.scrollTop = itemBottom - container.clientHeight
      }
    }
  }, [isHighlighted, scrollElement])

  return (
    <Flex
      ref={itemRef}
      align="center"
      justify="space-between"
      px="3"
      py="2"
      borderRadius="md"
      cursor="pointer"
      bg={
        isCurrent ? 'bg.emphasized' : isHighlighted ? 'bg.muted' : 'transparent'
      }
      outline={isHighlighted ? '1px solid' : 'none'}
      outlineColor={isHighlighted ? 'border.emphasized' : undefined}
      outlineOffset="-1px"
      _hover={{ backgroundColor: 'bg.emphasized' }}
      onClick={() => onSelect(pbx)}
      data-highlighted={isHighlighted || undefined}
    >
      <Text
        fontSize="sm"
        fontWeight={isCurrent ? '600' : '400'}
        color="fg.default"
      >
        {pbx.name}
      </Text>
      {isCurrent && <Icon as={Check} boxSize="4" color="fg.subtle" />}
    </Flex>
  )
}

function SmallListView({
  pbxs,
  currentPbxId,
  onSelect,
  noResultsText,
  highlightedPbxId,
  scrollElement,
}: {
  pbxs: PbxSummary[]
  currentPbxId?: number
  onSelect: (pbx: PbxSummary) => void
  noResultsText: string
  highlightedPbxId?: number
  scrollElement: HTMLDivElement | null
}) {
  return (
    <>
      {pbxs.length === 0 ? (
        <Text fontSize="sm" color="fg.subtle" px="3" py="4" textAlign="center">
          {noResultsText}
        </Text>
      ) : (
        pbxs.map((pbx) => (
          <PbxListItem
            key={pbx.id}
            pbx={pbx}
            isCurrent={pbx.id === currentPbxId}
            isHighlighted={pbx.id === highlightedPbxId}
            onSelect={() => onSelect(pbx)}
            scrollElement={scrollElement}
          />
        ))
      )}
    </>
  )
}

const ROW_HEIGHT = 40

function LargeListView({
  recentPbxs,
  mainPbxs,
  isSearching,
  searchLoading,
  currentPbxId,
  onSelect,
  recentLabel,
  allPbxsLabel,
  noResultsText,
  highlightedPbxId,
  scrollElement,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  recentPbxs: PbxSummary[]
  mainPbxs: PbxSummary[]
  isSearching: boolean
  searchLoading: boolean
  currentPbxId?: number
  onSelect: (pbx: PbxSummary) => void
  recentLabel: string
  allPbxsLabel: string
  noResultsText: string
  highlightedPbxId?: number
  scrollElement: HTMLDivElement | null
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  const showRecents = recentPbxs.length > 0 && !isSearching
  const showRows = !searchLoading && mainPbxs.length > 0

  const rowsContainerRef = useRef<HTMLDivElement>(null)
  const [scrollMargin, setScrollMargin] = useState(0)

  useLayoutEffect(() => {
    const rowsContainer = rowsContainerRef.current
    if (!scrollElement || !rowsContainer) return
    setScrollMargin(
      rowsContainer.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop,
    )
  }, [scrollElement, showRecents, recentPbxs.length, showRows])

  const virtualizer = useVirtualizer({
    count: mainPbxs.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    scrollMargin,
  })

  // Fetch next page when scrolling near the bottom.
  // We avoid putting getVirtualItems() in the dep array because it returns
  // a new reference every call. Instead we depend on the virtualizer's
  // internal range and trigger on scroll via the virtualizer's own reactivity.
  const virtualItems = virtualizer.getVirtualItems()
  const lastVirtualItemIndex = virtualItems.length
    ? virtualItems[virtualItems.length - 1].index
    : -1

  useEffect(() => {
    if (
      lastVirtualItemIndex >= mainPbxs.length - 10 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      onLoadMore()
    }
  }, [
    lastVirtualItemIndex,
    mainPbxs.length,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  ])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedPbxId == null) return
    const idx = mainPbxs.findIndex((p) => p.id === highlightedPbxId)
    if (idx >= 0) {
      virtualizer.scrollToIndex(idx, { align: 'auto' })
    }
  }, [highlightedPbxId, mainPbxs, virtualizer])

  return (
    <>
      {/* Recents (non-virtualized, always small) */}
      {showRecents && (
        <>
          <SectionHeader label={recentLabel} />
          {recentPbxs.map((pbx) => (
            <PbxListItem
              key={`recent-${pbx.id}`}
              pbx={pbx}
              isCurrent={pbx.id === currentPbxId}
              isHighlighted={pbx.id === highlightedPbxId}
              onSelect={() => onSelect(pbx)}
              scrollElement={scrollElement}
            />
          ))}
          <Separator my="1" borderColor="border.emphasized" />
        </>
      )}

      {/* Main virtualized list */}
      <SectionHeader label={allPbxsLabel} />
      {searchLoading ? (
        <Flex align="center" justify="center" py="4">
          <Spinner size="sm" color="fg.subtle" />
        </Flex>
      ) : mainPbxs.length === 0 ? (
        <Text fontSize="sm" color="fg.subtle" px="3" py="4" textAlign="center">
          {noResultsText}
        </Text>
      ) : (
        <div
          ref={rowsContainerRef}
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualRow) => {
            const pbx = mainPbxs[virtualRow.index]
            return (
              <div
                key={pbx.id}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                }}
              >
                <PbxListItem
                  pbx={pbx}
                  isCurrent={pbx.id === currentPbxId}
                  isHighlighted={pbx.id === highlightedPbxId}
                  onSelect={() => onSelect(pbx)}
                />
              </div>
            )
          })}
        </div>
      )}

      {isFetchingNextPage && (
        <Flex align="center" justify="center" py="2">
          <Spinner size="sm" color="fg.subtle" />
        </Flex>
      )}
    </>
  )
}
